import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import AddNewUserDrawer from "../../components/admin/users/AddNewUserDrawer";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
// import ImportUsersModal from "../../components/admin/users/ImportUsersModal";
import UsersTable from "../../components/admin/users/UsersTable";
import UserRole from "../../enums/Role";
import UserStatus from "../../enums/UserStatus";
import { Checkbox, PageContainer, SelectFilter } from "@outpave/ds";
import { useLocation, useNavigate } from "react-router-dom";
import { CHECK_ACTIVE_ADMINS } from "graphql/user";
import { useQuery } from "@apollo/client";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import Role from "../../enums/Role";
import {
  statusesFor,
  FILTERABLE_ROLES,
  type UserFilterData,
} from "utils/users/userFilters";

export type FormikUser = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  employeeGroup: string;
  status: string;
  address: {
    id: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  location: string;
  assignedManager: string;
  userPassword: string;
  role: string;
  manager: string;
  sendOneTimePassword: boolean;
  cards: string[];
};

const Users: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [showDeactivated, setShowDeactivated] = useState(false);
  const [roleFilter, setRoleFilter] = useState<UserRole[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);

  const [openAddNewUserDrawer, setOpenAddNewUserDrawer] =
    useState<boolean>(false);
  // const [openImportUsers, setOpenImportUsers] = useState<boolean>(false);
  // Holds the user and mode through the slide-out, which clearing them on close would blank.
  const profileDrawer = useDrawerRecord<{
    userId: string;
    edit: boolean;
    reactivate: boolean;
  }>();

  const {
    data: isAtLeastOneAdmin,
    loading: checkingForAdmins,
    refetch: refetchActiveAdminsCheck,
  } = useQuery(CHECK_ACTIVE_ADMINS);

  // Each control owns one query variable: the checkbox writes `status`, the
  // role filter writes `role`, search writes `searchTerm`. Nothing composes,
  // so nothing can silently overwrite anything else.
  const filterData: UserFilterData = useMemo(
    () => ({
      status: statusesFor(showDeactivated),
      role: roleFilter,
      searchTerm,
    }),
    [showDeactivated, roleFilter, searchTerm],
  );

  // The DS ships no i18n, so the three strings SelectFilter renders itself
  // come from here.
  const filterLabels = useMemo(
    () => ({
      search: (label: string) =>
        t("SelectFilterSearch", { label: label.toLowerCase() }),
      noMatches: (label: string, query: string) =>
        t("SelectFilterNoMatches", { label: label.toLowerCase(), query }),
      available: (total: number) => t("SelectFilterAvailable", { total }),
    }),
    [t],
  );

  const roleOptions = useMemo(
    () => FILTERABLE_ROLES.map((role) => ({ value: role, label: t(role) })),
    [t],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const id = params.get("id");
    const edit = params.get("edit") === "true";
    if (id) {
      handleOpenUserProfileDrawer(id, edit);
    }

    // `?add=true` opens the add-user drawer, so another page can send someone
    // here to create a user rather than hosting this drawer itself. The BYOC
    // dashboard's quick action uses it.
    //
    // The param is dropped once it has been acted on: left in place, going back
    // or forward would reopen a drawer the user had closed, and the URL would
    // keep telling anyone it was shared with to open it too.
    if (params.get("add") === "true") {
      handleOpenAddNewUserDrawer();
      params.delete("add");
      const search = params.toString();
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  const [refreshUsers, setRefreshUsers] = useState(0);

  const handleRefreshUsers = () => {
    setRefreshUsers((prev) => prev + 1);
    refetchActiveAdminsCheck();
  };

  const handleOpenAddNewUserDrawer = () => {
    setOpenAddNewUserDrawer(true);
  };

  const handleCloseOpenAddNewUserDrawer = () => {
    setOpenAddNewUserDrawer(false);
  };

  const handleOpenUserProfileDrawer = (
    userId: string,
    edit: boolean = false,
    reactivateUser: boolean = false,
  ) => {
    profileDrawer.show({ userId, edit, reactivate: reactivateUser });
  };

  const handleCloseOpenUserProfileDrawer = () => {
    profileDrawer.close();
  };

  const atLeastOneActiveAdminAlert =
    !checkingForAdmins && !isAtLeastOneAdmin?.checkActiveAdmins
      ? {
          severity: "warning" as const,
          message: t("AtLeastOneActiveAdminAlertMessage"),
        }
      : undefined;

  return (
    <PageContainer
      title={t("Users")}
      primaryAction={
        isAdminOrHigher && (
          <Button variant="primary-filled" onClick={handleOpenAddNewUserDrawer}>
            {t("Add User")}
          </Button>
        )
      }
      // TODO: restore when CSV import is implemented. Nothing reads the
      // chosen file yet, so the button opened a dialog that silently
      // discarded it. See the TODO on components/common/ImportCsvModal.
      // secondaryAction={
      //   isAdminOrHigher && (
      //     <Button
      //       variant="default-outlined"
      //       onClick={() => setOpenImportUsers(true)}
      //     >
      //       {t("Import")}
      //     </Button>
      //   )
      // }
      search={search}
      filters={
        <>
          <SelectFilter
            multiple
            label={t("Role")}
            options={roleOptions}
            selected={roleFilter}
            onChange={(values) => setRoleFilter(values as UserRole[])}
            labels={filterLabels}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showDeactivated}
                onChange={(event) => setShowDeactivated(event.target.checked)}
              />
            }
            label={t("Show deactivated users")}
          />
        </>
      }
      alert={
        atLeastOneActiveAdminAlert && (
          <Alert severity={atLeastOneActiveAdminAlert.severity} role="status">
            {atLeastOneActiveAdminAlert.message}
          </Alert>
        )
      }
    >
      <UsersTable
        filterData={filterData}
        handleOpenUserProfileDrawer={handleOpenUserProfileDrawer}
        refreshUsers={refreshUsers}
        handleRefreshUsers={handleRefreshUsers}
      />

      <AddNewUserDrawer
        open={openAddNewUserDrawer}
        handleCloseDrawer={handleCloseOpenAddNewUserDrawer}
        handleCreatedUser={handleRefreshUsers}
      />

      {profileDrawer.record && (
        <UserProfileDrawer
          selectedUserId={profileDrawer.record.userId}
          open={profileDrawer.open}
          edit={profileDrawer.record.edit}
          isReactivateUser={profileDrawer.record.reactivate}
          handleCloseDrawer={handleCloseOpenUserProfileDrawer}
          onAfterClose={profileDrawer.afterClose}
          handleUpdateUser={handleRefreshUsers}
        />
      )}

      {/* <ImportUsersModal
        open={openImportUsers}
        onClose={() => setOpenImportUsers(false)}
      /> */}
    </PageContainer>
  );
};

export default Users;
